import React, { useCallback, useRef, useState } from "react";
import Main3DManager from "./main.3d.manager";
import "./styles/art.space.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useArtSpace from "../../hooks/useArtSpace";
import useEvents from "../../hooks/useEvents";
import { useSelector } from "react-redux";
import { AppState } from "../../store/root.reducer";
import { ApiClient } from "../../utils/apiClient";
import User from "../../models/user.model";
import { ToastContainer, toast } from "react-toastify";

import AppData from "./app.data";
import { Artwork, ArtworkWrapper } from "@/types/artworksData";
import { Slot } from "@/types/slotsData";
import { GallerySetupModal } from "../modals/gallery.setup";
import { resolveActiveMoodIndex } from "../../utils/moodVisibility";
import {
  buildTelemetrySpaceContext,
  trackArtworkView,
  trackGalleryView,
  type TelemetrySpaceContext,
} from "../../utils/telemetry";
import { useGalleryChromeContext } from "../../contexts/GalleryChromeContext";
import { isGalleryBackgroundMusicPlayable } from "../../utils/visitorPreferences";
const appData = new AppData()

const BASE_API = process.env.REACT_APP_BASE_API;
const apiClient = ApiClient(BASE_API);

const PersonalMoMComponent = React.memo(() => {
  const { id } = useParams()
  const user: User = useSelector((state: AppState) => state.auth.user);
  appData.setUserData(user)
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const { listedVouchers } = useSelector((state: AppState) => state.nft);
  const galleryTelemetryContextRef = useRef<TelemetrySpaceContext>({});
  const loadedGalleryIdRef = useRef<string | null>(null);
  const trackedGalleryViewRef = useRef<string | null>(null);
  const { setHasPlayableGalleryBackgroundMusic } = useGalleryChromeContext();
  const [renderSettingsModal, setRenderSettingsModal] = useState<boolean>(false);
  const [moods, setMoods] = useState<any[]>([]);

  const [gallerySetupModal, setGallerySetupModal] = useState<boolean>(false);
  const [published, setPublished] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [renderQuality, setRenderQuality] = useState<"LQ" | "MQ" | "HQ">(() => {
    if (typeof window === "undefined") return "MQ";
    const stored = localStorage.getItem("renderQuality");
    if (stored === "LQ" || stored === "MQ" || stored === "HQ") {
      return stored as "LQ" | "MQ" | "HQ";
    }
    localStorage.setItem("renderQuality", "MQ");
    return "MQ";
  });

  const [main3DManager, setMain3DManager] = React.useState<
    Main3DManager | undefined
  >();

  const successNotify = (msg: any) => {
    toast.success(msg, {
      position: "bottom-left",
    })
  }

  const failedNotify = (msg: string) => {
    toast.error(msg, {
      position: "bottom-left",
    })
  }

  const onDioramaButton = useCallback((slot: Slot) => {
    try {
      const selectedArtwork = slot.artworkWrapper.artworks[slot.artworkWrapper.activeArtwork]
      navigate(`/gallery/${selectedArtwork.galleryId}?t=personal`)
    } catch (e) {
      console.log(e); // Logs the error
    }
  }, []);

  const onArtworkClicked = useCallback((artwork: ArtworkWrapper) => {
    const activeArtworkIndex = Number(artwork?.activeArtwork);
    const selectedArtwork = artwork?.artworks?.[Number.isFinite(activeArtworkIndex) ? activeArtworkIndex : 0];
    if (selectedArtwork?._id && loadedGalleryIdRef.current) {
      trackArtworkView(
        String(selectedArtwork._id),
        `${location.pathname}${location.search || ""}`,
        loadedGalleryIdRef.current,
        "personal",
        galleryTelemetryContextRef.current
      );
    }
    console.log("Clicked on artwork with data: ", artwork)
  }, [location.pathname, location.search]);

  React.useEffect(() => {

    (async () => {
      if (user) {
        const gallery = await apiClient.getSellerMomGallery();
      }
    })()
    let _main3D = new Main3DManager();
    appData.setMain3DManager(_main3D);

    setMain3DManager(_main3D);

    return () => {
      _main3D.destroy();
      main3DManager?.destroy();
      appData.setMain3DManager(null);
    };
  }, [user]);

  React.useEffect(() => {
    if (main3DManager && canvasRef.current) {
      main3DManager.init(canvasRef.current);
      main3DManager.setDioramaClickEvent(onDioramaButton);
      main3DManager.setArtworkClickEvent(onArtworkClicked);
    }
  }, [main3DManager, canvasRef, onArtworkClicked, onDioramaButton]);

  useEvents(main3DManager, canvasRef, listedVouchers);

  React.useEffect(() => {
    let cancelled = false;

    const apiClient = ApiClient(BASE_API);
    if (main3DManager) {
      setHasPlayableGalleryBackgroundMusic(false);
      const init3D = async () => {
        try {
          let galleryResp = await apiClient.getSellerMomGallery();

          if (galleryResp && !cancelled) {
            // load gallery data
            const { gallery } = galleryResp;
            const spaceData = await apiClient.getSpaceById(gallery.spaceId)
            if (cancelled) return;
            const fallbackMood =
              Number.isInteger(gallery?.data?.mood) ? gallery.data.mood : undefined;
            const resolvedMood = resolveActiveMoodIndex(
              spaceData?.space?.data?.moods,
              fallbackMood,
              {
                userId: user?._id,
                galleryId: gallery?._id
              }
            );
            if (!gallery.data) {
              gallery.data = {} as any;
            }
            gallery.data.mood = resolvedMood;
            setHasPlayableGalleryBackgroundMusic(
              isGalleryBackgroundMusicPlayable(gallery.data?.sound)
            );

            appData.setGalleryData(gallery)
            appData.setSpaceData(spaceData.space)
            const telemetryContext = buildTelemetrySpaceContext(gallery, spaceData.space);
            galleryTelemetryContextRef.current = telemetryContext;
            loadedGalleryIdRef.current = gallery?._id ? String(gallery._id) : null;
            const galleryTelemetryKey = `${loadedGalleryIdRef.current}:${location.pathname}${location.search || ""}`;
            if (loadedGalleryIdRef.current && trackedGalleryViewRef.current !== galleryTelemetryKey) {
              trackedGalleryViewRef.current = galleryTelemetryKey;
              trackGalleryView(
                loadedGalleryIdRef.current,
                `${location.pathname}${location.search || ""}`,
                "personal",
                telemetryContext
              );
            }
            setMoods(spaceData.space.data.moods || []);
            setPublished(gallery.published);
            setTitle(gallery.title);

            console.log("%cLoading data: ", "color:yellow", appData)
            await main3DManager.loadGallery()
          }
        } catch (error) {
          console.error("PersonalGallery: Error initializing 3D scene", error);
        }
      }
      init3D()
    }

    return () => {
      cancelled = true;
      if (main3DManager) {
        main3DManager.dispose();
      }
      setHasPlayableGalleryBackgroundMusic(false);
    };
  }, [location.pathname, location.search, main3DManager, setHasPlayableGalleryBackgroundMusic, user?._id]);

  const onChangeMood = async (newMood: any, local?: any) => {
    if (local) {
      await main3DManager.changeMood(newMood, local);
    } else {
      window.location.reload();
    }

  }

  const onQualityChanged = (quality: "LQ" | "MQ" | "HQ") => {
    localStorage.setItem('renderQuality', quality);
    window.location.reload();
  }

  return (
    <>
      <div id="WebGL" draggable="false" ref={canvasRef}></div>
      {/* <div id="prompt-text"></div> */}
      <div id="loading" className="loading-screen">Loading ...</div>
      <div id="joystick-background"></div>
      <div id="joystick-thumb"></div>
      <ToastContainer />
    </>
  );
});

export { PersonalMoMComponent };
